import { useEffect, useMemo, useState, type ReactNode } from "react";
import { getInitialWebsiteOfferingPlugins, loadWebsiteOfferingPlugins } from "./registry";
import type { WebsiteProductOfferingPlugin } from "./types";
import { OfferingsContext } from "./offerings-context";

export function OfferingsProvider({ children }: { children: ReactNode }) {
  const [offerings, setOfferings] = useState<WebsiteProductOfferingPlugin[]>(
    getInitialWebsiteOfferingPlugins
  );
  const [isLoading, setIsLoading] = useState(() => getInitialWebsiteOfferingPlugins().length === 0);

  useEffect(() => {
    let mounted = true;

    // Offering plugins carry React component factories (productPage, signupPage,
    // etc.), which cannot cross a JSON boundary — so a server endpoint can only
    // ever return inert manifests. Discovery therefore runs client-side via
    // import.meta.glob, in prod and dev alike; the eager plugins already seed the
    // initial state, and this refresh picks up any lazily-loaded plugin modules.
    void (async () => {
      try {
        const loaded = await loadWebsiteOfferingPlugins();
        if (mounted) setOfferings(loaded);
      } catch (err) {
        console.error("Failed to load offerings via local discovery:", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      offerings,
      isLoading,
    }),
    [offerings, isLoading]
  );

  return <OfferingsContext.Provider value={value}>{children}</OfferingsContext.Provider>;
}
